import { Search } from "lucide-react";
import { useId, useState } from "react";

import type { PersonRef } from "../api/types";
import { useDebouncedValue } from "../lib/hooks";
import { Avatar } from "../ui/Avatar";
import { usePeopleSearch } from "./api";

/**
 * Search people (or only staff) and pick one. The list shows only while the search box is in
 * use: it closes when focus leaves the picker, on Escape, and after a pick.
 */
export function PersonPicker({
  label,
  kind = "people",
  exclude = [],
  onPick,
}: {
  label: string;
  kind?: "people" | "staff";
  exclude?: string[];
  onPick: (person: PersonRef) => void;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const q = useDebouncedValue(search.trim(), 200);
  const results = usePeopleSearch(q, kind);
  const minimum = kind === "people" ? 2 : 0;
  const matches = (results.data ?? []).filter((p) => !exclude.includes(p.id));
  const showResults = open && q.length >= minimum && (kind === "staff" || search.length > 0);

  return (
    <div
      className="person-picker"
      onBlur={(event) => {
        // Moving focus to one of the results keeps it open; anywhere else closes it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <label className="visually-hidden" htmlFor={id}>
        {label}
      </label>
      <div className="search">
        <Search className="search-icon" size={16} aria-hidden="true" />
        <input
          id={id}
          className="input"
          type="search"
          placeholder={label}
          value={search}
          maxLength={100}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
          }}
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);
          }}
        />
      </div>
      {showResults ? (
        matches.length ? (
          <ul className="picker-results" aria-label="Matching people">
            {matches.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  className="picker-result"
                  onClick={() => {
                    onPick(person);
                    setSearch("");
                    setOpen(false);
                  }}
                >
                  <Avatar name={person.name} seed={person.id} size="sm" />
                  <span className="cell-person-text">
                    <span className="cell-strong">{person.name}</span>
                    <span className="cell-secondary">{person.email}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : results.isFetching ? null : (
          <p className="muted picker-empty">No one matches.</p>
        )
      ) : null}
    </div>
  );
}
